import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/outline";
import AdmittedPatientsModal from "../admission/AdmittedPatientsModal";
import CommonButton from "../../common11/Buttons/CommonButton";
import CommonModal from "../../common11/commonModals/CommonModal";
import CommonDatePicker from "../../common11/formInputs/CommonDatePicker";
import CommonTimePicker from "../../common11/formInputs/CommonTimePicker";
import InputAreaField from "../../common11/formInputs/InputAreaField";
import SearchDropdown from "../../common11/formInputs/SearchDropdown";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../common11/toastUtils";
import IpdPatientSearchPanel, {
  IpdPatientInfoCard,
  useIpdPatientSelection,
} from "../shared/IpdPatientSearchPanel";
import {
  getDefaultDateTime,
  MOCK_NOTE_TEMPLATE_OPTIONS,
  MOCK_STAFF_OPTIONS,
} from "../shared/ipdPatientUtils";
import {
  listAdmittedPatientsForDoctorRound,
  mapFormToSavePayload,
  mapListRowForTable,
  mapRecordToFormValues,
  mapWebPatientToAdmissionRow,
  toLookupOption,
} from "../../../services/nursingNotesService";
import {
  clearEditing,
  fetchNursingNoteById,
  fetchNursingNotesList,
  fetchNursingStaff,
  fetchNursingTemplates,
  saveNursingNote,
  setPatient,
} from "../../../store/slices/nursingNotesSlice";

const NOTE_LIST_COLUMNS = [
  { key: "srNo", label: "Sr No." },
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "templateName", label: "Template Name" },
  { key: "notes", label: "Notes" },
  { key: "staffName", label: "Staff" },
];

const mapAdmissionRowToPatient = (row) => ({
  uhid: row.uhid || "",
  patientName: row.patientName || "",
  contact: row.contact || "",
  patientGender: row.gender || "",
  patientAge: row.age || "",
  doctorName: row.doctorName || "",
  doctorId: row.doctorId != null ? Number(row.doctorId) : null,
  wardName: row.wardName || "",
  bedNo: row.bedNo || "",
  admissionId: Number(row.admissionId) || 0,
  regId: row.regId != null ? Number(row.regId) : null,
});

function ClinicalNotesScreen({
  screenTitle,
  notesLabel,
  staffLabel,
  staffOptions = MOCK_STAFF_OPTIONS,
  isNursingNotes = true,
  useLiveApi = false,
}) {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.user);
  const {
    patient: livePatient,
    records,
    templates,
    staff,
    editingId,
    loading,
    saving,
  } = useSelector((state) => state.nursingNotes);

  const mockSelection = useIpdPatientSelection();
  const { date: defaultDate, time: defaultTime } = getDefaultDateTime();

  const [notesList, setNotesList] = useState([]);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [patientsModalOpen, setPatientsModalOpen] = useState(false);
  const [admissionRows, setAdmissionRows] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);

  const defaultValues = {
    patientSearch: null,
    filterFromDate: defaultDate,
    filterToDate: defaultDate,
    notesBy: null,
    templateName: null,
    date: defaultDate,
    time: defaultTime,
    notes: "",
    notesAttachments: [],
    staffName: null,
  };

  const { control, handleSubmit, reset, watch, setValue, getValues } = useForm({
    defaultValues,
  });

  const patient = useLiveApi ? livePatient : mockSelection.patient;
  const formBusy = useLiveApi && (loading || saving);

  const templateOptions = useMemo(() => {
    if (!useLiveApi) return MOCK_NOTE_TEMPLATE_OPTIONS;
    return (templates || []).map(toLookupOption).filter(Boolean);
  }, [useLiveApi, templates]);

  const staffDropdownOptions = useMemo(() => {
    if (!useLiveApi) return staffOptions;
    return (staff || []).map(toLookupOption).filter(Boolean);
  }, [useLiveApi, staff, staffOptions]);

  const filterFromDate = watch("filterFromDate");
  const filterToDate = watch("filterToDate");
  const notesBy = watch("notesBy");
  const notesAttachments = watch("notesAttachments") || [];

  const tableRows = useMemo(() => {
    if (useLiveApi) {
      return (records || []).map((row, index) => mapListRowForTable(row, index));
    }
    return notesList.filter((row) => {
      const matchesFrom = !filterFromDate || row.date >= filterFromDate;
      const matchesTo = !filterToDate || row.date <= filterToDate;
      const matchesStaff = !notesBy?.value || row.staffName === notesBy.label;
      return matchesFrom && matchesTo && matchesStaff;
    });
  }, [
    useLiveApi,
    records,
    notesList,
    filterFromDate,
    filterToDate,
    notesBy,
  ]);

  const loadIpPatients = useCallback(async () => {
    if (!useLiveApi) return;
    setPatientsLoading(true);
    try {
      const data = await listAdmittedPatientsForDoctorRound({
        isDischarged: false,
      });
      setAdmissionRows((data || []).map(mapWebPatientToAdmissionRow));
    } catch (error) {
      setAdmissionRows([]);
      showErrorToast(error.message || "Failed to load admitted patients");
    } finally {
      setPatientsLoading(false);
    }
  }, [useLiveApi]);

  useEffect(() => {
    loadIpPatients();
  }, [loadIpPatients]);

  useEffect(() => {
    if (!useLiveApi) return;
    dispatch(fetchNursingTemplates(""));
    dispatch(fetchNursingStaff(""));
  }, [useLiveApi, dispatch]);

  const patientSearchOptions = useMemo(
    () =>
      admissionRows
        .filter((row) => row.status === "Admitted")
        .map((row) => ({
          id: row.admissionId,
          label: [
            row.patientName,
            row.uhid,
            row.age ? `${row.age}Y` : null,
            row.doctorName,
          ]
            .filter(Boolean)
            .join(" | "),
          value: row.patientName,
          row,
        })),
    [admissionRows]
  );

  const handleHandwritingImage = useCallback(
    (dataUrl) => {
      const current = getValues("notesAttachments") || [];
      setValue(
        "notesAttachments",
        [...current, { id: `${Date.now()}-${current.length}`, dataUrl }],
        { shouldDirty: true }
      );
    },
    [getValues, setValue]
  );

  const handleRemoveAttachment = useCallback(
    (id) => {
      const current = getValues("notesAttachments") || [];
      setValue(
        "notesAttachments",
        current.filter((item) => item.id !== id),
        { shouldDirty: true }
      );
    },
    [getValues, setValue]
  );

  const handleSelectLivePatient = (row) => {
    if (!row) {
      dispatch(setPatient(null));
      setValue("patientSearch", null);
      return;
    }
    const mapped = mapAdmissionRowToPatient(row);
    dispatch(setPatient(mapped));
    setValue("patientSearch", {
      id: mapped.admissionId || mapped.uhid,
      label: mapped.patientName || "",
      value: mapped.contact || mapped.uhid || "",
    });
    dispatch(clearEditing());
  };

  const handleLivePatientSearchSelect = (selectedOption) => {
    if (!selectedOption) {
      handleSelectLivePatient(null);
      return;
    }
    if (selectedOption.row) {
      handleSelectLivePatient(selectedOption.row);
    }
  };

  const handleReset = () => {
    const { date, time } = getDefaultDateTime();
    reset({
      ...defaultValues,
      date,
      time,
      filterFromDate: date,
      filterToDate: date,
      notesAttachments: [],
      patientSearch: patient
        ? {
            id: patient.admissionId || patient.uhid,
            label: patient.patientName,
            value: patient.contact || patient.uhid,
          }
        : null,
    });
    if (useLiveApi) {
      dispatch(clearEditing());
    }
  };

  const handleShow = async () => {
    if (!patient?.patientName) {
      showWarningToast("Please select a patient first");
      return;
    }
    if (!useLiveApi) {
      setListModalOpen(true);
      return;
    }
    if (!patient.admissionId) {
      showWarningToast("Selected patient is missing admission id");
      return;
    }
    setListModalOpen(true);
    const result = await dispatch(
      fetchNursingNotesList({
        admissionId: patient.admissionId,
        fromDate: filterFromDate,
        toDate: filterToDate,
        notesById: notesBy?.value ?? notesBy?.id ?? null,
        isNursingNotes,
      })
    );
    if (fetchNursingNotesList.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load notes");
    }
  };

  const handleEditRow = async (row) => {
    if (!useLiveApi || !row?.nursingRmoNotesId || formBusy) return;
    const result = await dispatch(fetchNursingNoteById(row.nursingRmoNotesId));
    if (fetchNursingNoteById.rejected.match(result)) {
      showErrorToast(result.payload || "Failed to load note");
      return;
    }
    const formValues = mapRecordToFormValues(result.payload);
    if (!formValues) return;
    setValue("date", formValues.date);
    setValue("time", formValues.time);
    setValue("notes", formValues.notes);
    setValue("templateName", formValues.templateName);
    setValue("staffName", formValues.staffName);
    setValue("notesAttachments", []);
    setListModalOpen(false);
    showSuccessToast("Note loaded for edit");
  };

  const handleSave = handleSubmit(async (data) => {
    if (formBusy) return;
    if (!patient?.patientName) {
      showWarningToast("Please select a patient first");
      return;
    }

    const attachments = data.notesAttachments || [];
    const hasText = Boolean(data.notes?.trim());
    const hasAttachments = attachments.length > 0;

    if (!useLiveApi) {
      if (!hasText && !hasAttachments) {
        showWarningToast(`Please enter ${notesLabel.toLowerCase()} or add handwriting`);
        return;
      }
      const entry = {
        id: `${Date.now()}`,
        srNo: String(notesList.length + 1),
        date: data.date,
        time: data.time,
        templateName: data.templateName?.label || "—",
        notes: data.notes || "",
        attachments,
        staffName: data.staffName?.label || "—",
      };
      setNotesList((prev) => [entry, ...prev]);
      showSuccessToast(`${screenTitle} saved successfully`);
      reset({
        ...data,
        templateName: null,
        notes: "",
        notesAttachments: [],
        staffName: null,
      });
      return;
    }

    if (!patient.admissionId) {
      showWarningToast("Selected patient is missing admission id");
      return;
    }
    if (!data.templateName?.value && !data.templateName?.id) {
      showWarningToast("Please select a template");
      return;
    }
    if (!data.staffName?.value && !data.staffName?.id) {
      showWarningToast(`Please select ${staffLabel.toLowerCase()}`);
      return;
    }
    if (!hasText) {
      showWarningToast(`Please enter ${notesLabel.toLowerCase()}`);
      return;
    }

    const userId = Number(authUser?.userId || 1);
    const payload = mapFormToSavePayload(data, patient, userId, isNursingNotes, {
      isUpdate: Boolean(editingId),
    });

    const result = await dispatch(
      saveNursingNote({
        id: editingId,
        payload,
      })
    );

    if (saveNursingNote.rejected.match(result)) {
      showErrorToast(result.payload || `Failed to save ${screenTitle}`);
      return;
    }

    showSuccessToast(
      editingId
        ? `${screenTitle} updated successfully`
        : `${screenTitle} saved successfully`
    );
    handleReset();
  });

  const notesFormSection = (
    <div className="space-y-3">
      <div className="rounded border border-gray-200 p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <CommonDatePicker
            name="filterFromDate"
            control={control}
            label="From Date"
            type="date"
            fontSize="text-xs"
            isFloatingLabel
          />
          <CommonDatePicker
            name="filterToDate"
            control={control}
            label="To Date"
            type="date"
            fontSize="text-xs"
            isFloatingLabel
          />
          <SearchDropdown
            name="notesBy"
            control={control}
            label="Notes By"
            dataArray={staffDropdownOptions}
            placeholder="Select"
            isSearchable
            isClearable
            fontSize="text-xs"
            isFloatingLabel
          />
          <CommonButton
            label="Show"
            type="button"
            size="sm"
            onClick={handleShow}
            disabled={formBusy}
            className="bg-white text-red-600 border border-red-400 hover:bg-red-50 text-xs sm:text-sm w-full"
          />
        </div>
      </div>

      <div className="rounded border border-gray-200 p-3 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SearchDropdown
            name="templateName"
            control={control}
            label="Template Name"
            dataArray={templateOptions}
            placeholder="Select"
            isSearchable
            isClearable
            fontSize="text-xs"
            isFloatingLabel
          />
          <CommonDatePicker
            name="date"
            control={control}
            label="Date"
            type="date"
            fontSize="text-xs"
            isFloatingLabel
          />
          <CommonTimePicker
            name="time"
            control={control}
            label="Time"
            fontSize="text-xs"
            isFloatingLabel
            use24Hour={false}
            format="hh:mm:ss A"
          />
        </div>

        <div className="space-y-2">
          <InputAreaField
            name="notes"
            control={control}
            label={notesLabel}
            rows={12}
            fontSize="text-xs"
            inputBgColor="bg-white"
            enableSpeechToText
            enableHandwriting
            onHandwritingImage={handleHandwritingImage}
          />

          {notesAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {notesAttachments.map((item) => (
                <div
                  key={item.id}
                  className="relative group rounded border border-gray-300 bg-gray-50 p-1"
                >
                  <img
                    src={item.dataUrl}
                    alt="Handwriting attachment"
                    className="h-16 w-auto max-w-[120px] object-contain rounded"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(item.id)}
                    title="Remove handwriting image"
                    aria-label="Remove handwriting image"
                    className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 shadow-sm hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <SearchDropdown
          name="staffName"
          control={control}
          label={staffLabel}
          dataArray={staffDropdownOptions}
          placeholder="Select"
          isSearchable
          isClearable
          fontSize="text-xs"
          isFloatingLabel
        />
      </div>
    </div>
  );

  const listSection = (
    <div className="rounded border border-gray-300">
      <div className="overflow-auto max-h-[60vh]">
        <table className="min-w-full text-[11px]">
          <thead className="bg-teal-700 text-white sticky top-0">
            <tr>
              {NOTE_LIST_COLUMNS.map((col) => (
                <th key={col.key} className="px-2 py-1.5 text-left font-semibold">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {useLiveApi && loading ? (
              <tr>
                <td
                  colSpan={NOTE_LIST_COLUMNS.length}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  Loading notes…
                </td>
              </tr>
            ) : tableRows.length === 0 ? (
              <tr>
                <td
                  colSpan={NOTE_LIST_COLUMNS.length}
                  className="px-2 py-6 text-center text-gray-500 border-t border-gray-200"
                >
                  No notes found.
                </td>
              </tr>
            ) : (
              tableRows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-t border-gray-200 ${
                    useLiveApi ? "hover:bg-teal-50 cursor-pointer" : ""
                  }`}
                  onClick={() => handleEditRow(row)}
                >
                  {NOTE_LIST_COLUMNS.map((col) => (
                    <td key={col.key} className="px-2 py-1.5 text-gray-700 align-top">
                      {col.key === "notes" ? (
                        <div className="space-y-1">
                          {row.notes ? (
                            <span className="line-clamp-2">{row.notes}</span>
                          ) : (
                            <span className="text-gray-400 italic">Handwriting only</span>
                          )}
                          {row.attachments?.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1">
                              {row.attachments.slice(0, 3).map((att) => (
                                <img
                                  key={att.id}
                                  src={att.dataUrl}
                                  alt=""
                                  className="h-8 w-auto max-w-[48px] object-contain rounded border border-gray-200 bg-white"
                                />
                              ))}
                              {row.attachments.length > 3 && (
                                <span className="text-[10px] text-gray-500">
                                  +{row.attachments.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        row[col.key] || "—"
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="p-2 sm:p-3 pb-24">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="sticky top-0 z-10 rounded-t-lg px-3 py-2 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-[#217E90]">{screenTitle}</h2>
        </div>

        <div className="p-3 space-y-3">
          {useLiveApi ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
                <div className="w-full sm:w-[40%]">
                  <SearchDropdown
                    name="patientSearch"
                    control={control}
                    label="Search by patient name or mobile number"
                    dataArray={patientSearchOptions}
                    placeholder="Search by patient name or UHID"
                    isSearchable
                    isClearable
                    fontSize="text-xs sm:text-sm"
                    isFloatingLabel
                    onChange={handleLivePatientSearchSelect}
                  />
                </div>
                <div className="flex-shrink-0">
                  <CommonButton
                    label="Admitted Patients"
                    type="button"
                    size="sm"
                    className="bg-[#217E90] text-white hover:bg-[#1a5e70] px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto"
                    onClick={() => setPatientsModalOpen(true)}
                    disabled={patientsLoading}
                  />
                </div>
              </div>
              <IpdPatientInfoCard patient={patient} />
            </>
          ) : (
            <IpdPatientSearchPanel
              patient={mockSelection.patient}
              control={mockSelection.control}
              patientSearchOptions={mockSelection.patientSearchOptions}
              onPatientSearchSelect={mockSelection.handlePatientSearchSelect}
              onSelectPatient={mockSelection.handleSelectPatient}
            />
          )}
          {patient && notesFormSection}
        </div>
      </div>

      {useLiveApi && (
        <AdmittedPatientsModal
          isOpen={patientsModalOpen}
          onClose={() => setPatientsModalOpen(false)}
          onSelectPatient={handleSelectLivePatient}
          onRowDoubleClick={handleSelectLivePatient}
          rows={admissionRows}
        />
      )}

      <CommonModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        title={`${screenTitle} List`}
        size="lg"
        maxHeightVh={90}
        paperSx={{
          width: { xs: "calc(100vw - 16px)", lg: "min(90vw, 960px)" },
          maxWidth: { lg: "min(90vw, 960px)" },
        }}
        disableBackdropClose={false}
        showCloseButton
        actions={[]}
      >
        {listSection}
      </CommonModal>

      <div className="fixed bottom-4 right-4 z-50 flex flex-wrap justify-end gap-2 sm:gap-3 pr-2 sm:pr-6">
        <CommonButton
          label={`${screenTitle} List`}
          onClick={handleShow}
          variant="reset-outlined"
          size="sm"
          type="button"
          className="px-3 md:px-5 text-xs md:text-sm rounded-full bg-emerald-50 text-emerald-700 border border-emerald-400 hover:bg-emerald-100"
        />
        <CommonButton
          label="Reset"
          onClick={handleReset}
          variant="danger-outline"
          size="sm"
          type="button"
          disabled={formBusy}
          className="px-3 md:px-5 text-xs md:text-sm"
        />
        <CommonButton
          label={
            saving
              ? "Saving…"
              : useLiveApi && editingId
                ? "Update"
                : "Save"
          }
          onClick={handleSave}
          variant="primary-filled"
          size="sm"
          type="button"
          disabled={formBusy}
        />
      </div>
    </div>
  );
}

export default ClinicalNotesScreen;
